import type { ComponentType, CSSProperties } from 'react'
import clsx from 'clsx'

import { Calendar, type IconProps } from '../icons'
import { LensClock, Page, Plus, Tick, Tray } from './icons'
import { lensCount, type LensId, type TaskBoard } from './derive'
import { ListDot, PendingControl } from './parts'

/**
 * THE LENS COLUMN — 196px, the left of the three columns (README §7).
 *
 * A lens is a QUESTION ABOUT THE SAME TASKS, not a folder: nothing moves when you switch, and
 * every count is derived from the one list. That is why they can overlap — an unfiled task with
 * no date is in Inbox and in Someday at once, and both counts are true.
 */
interface Lens {
  id: LensId
  label: string
  icon: ComponentType<IconProps>
  /** The one lens whose glyph is not the neutral grey: Done log wears the status green. */
  hue?: string
  /** What this lens says when it holds nothing. Never "no tasks" — that is a different claim. */
  empty: string
}

export const LENSES: Lens[] = [
  {
    id: 'inbox',
    label: 'Inbox',
    icon: Tray,
    empty: 'Nothing unfiled. Anything captured without a list lands here first.',
  },
  {
    id: 'today',
    label: 'Today',
    icon: LensClock,
    empty: 'Nothing is due today, and nothing is due in the next seven days.',
  },
  {
    id: 'upcoming',
    label: 'Upcoming',
    icon: Calendar,
    empty: 'Nothing is dated after today. Undated work is under Someday.',
  },
  {
    id: 'someday',
    label: 'Someday',
    icon: Page,
    empty: 'Nothing undated. Someday holds the tasks that carry no date at all.',
  },
  {
    id: 'done',
    label: 'Done log',
    icon: Tick,
    hue: 'text-success-dot',
    empty: 'Nothing finished yet. Completions are kept here for 90 days.',
  },
]

export function LensColumn({
  board,
  active,
  onSelect,
}: {
  board: TaskBoard | undefined
  active: LensId
  onSelect: (lens: LensId) => void
}) {
  return (
    <nav aria-label="Task lenses" className="flex w-[196px] flex-none flex-col gap-1">
      {LENSES.map((lens) => {
        const isActive = lens.id === active
        const count = board ? lensCount(board, lens.id) : null
        const Icon = lens.icon
        return (
          <button
            key={lens.id}
            type="button"
            aria-current={isActive ? 'page' : undefined}
            onClick={() => onSelect(lens.id)}
            style={
              { '--mo-rule': isActive ? 'var(--color-surface)' : 'var(--color-tasks)' } as CSSProperties
            }
            className={clsx(
              'mo-rule flex items-center gap-[10px] rounded-tile px-3 py-[9px] text-left',
              isActive ? 'bg-ink' : 'border border-line bg-surface',
            )}
          >
            <Icon
              size={15}
              strokeWidth={lens.id === 'done' ? 1.9 : 1.8}
              className={clsx(
                'flex-none',
                isActive ? 'text-tasks-lift' : (lens.hue ?? 'text-muted'),
              )}
            />
            <span
              className={clsx(
                'flex-1 text-[12.5px] leading-none',
                isActive ? 'font-semibold text-surface' : 'font-medium text-ink-2',
              )}
            >
              {lens.label}
            </span>
            {count !== null && (
              <span className="text-[11px] font-semibold leading-none text-muted">{count}</span>
            )}
          </button>
        )
      })}

      <div className="mx-[2px] mb-2 mt-[11px] h-px bg-line" aria-hidden />
      <div className="px-3 pb-[6px] text-[9.5px] font-semibold leading-none tracking-[0.08em] text-microcopy">
        LISTS
      </div>

      {/* The lists are DERIVED from the open tasks, so a list nobody has anything in stops being
          drawn. There is no list registry to fall out of step with the tasks themselves. */}
      {board?.lists.map((row) => (
        <div key={row.name} className="flex items-center gap-[10px] px-3 py-[7px]">
          <ListDot list={row.name} />
          <span className="flex-1 truncate text-[12px] font-medium leading-none text-ink-2">
            {row.name}
          </span>
          <span className="text-[11px] font-normal leading-none text-microcopy">{row.open}</span>
        </div>
      ))}

      <PendingControl
        label={<span className="flex-1 text-left">New list</span>}
        waitingFor="Making a new list arrives with V6 (Reminders), when lists get a backend of their own. Until then the lists here are the ones your tasks are already filed under."
        icon={<Plus size={13} className="flex-none text-faintest-2" />}
        className="gap-[10px] px-3 py-[7px] text-[12px] font-medium text-microcopy"
      />
    </nav>
  )
}
